<template>
    <DeepiBaseField
        :id="`${props.label?.replace(/\s+/g, '-').toLowerCase()}-${id}`"
        :hasFocus="hasFocus || props.focusHelper"
        :isValid="textField?.isValid ?? true"
        :errorMessages="textField?.errorMessages ?? []"
        :label="props.label"
        :hint="props.hint"
        :forceDetails="props.forceDetails"
        :rules="props.rules"
        :variant="props.variant"
    >
        <v-text-field
            ref="textField"
            class="deepi-text-field"
            variant="solo"
            validateOn="lazy blur"
			:id="`${props.label?.replace(/\s+/g, '-').toLowerCase()}-${id}`"
            :flat="true"
            :hideDetails="true"
            :class="classes"
            :focused="hasFocus || props.focusHelper"
            :rules="props.rules"
            :prefix="props.prefix"
            :placeholder="props.placeholder"
            :type="props.type"
            :validationValue="props.validationValue"
            :modelValue="props.modelValue"
            @update:modelValue="emit('update:modelValue', $event)"
            @update:focused="hasFocus = $event"
            @keydown.stop="props.validateKeyDown?.($event)"
        >
            <template
                v-for="(_, name) in $slots"
                v-slot:[name]="slotData"
            >
                <slot
                :name="name"
                v-bind="slotData"
                />
            </template>
            <template
                v-if="props.prependIcon"
                #prepend-inner
            >
                <v-icon
                    :size="18"
                    :color="iconColor"
                    :icon="props.prependIcon"
                />
            </template>
        </v-text-field>
    </DeepiBaseField>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { type Dayjs } from 'dayjs';
import { type VTextField } from 'vuetify/components';

import DeepiBaseField from '../baseField/DeepiBaseField.vue';

const id = Math.random();

export interface DeepiTextFieldProps {
    label?: string;
    prependIcon?: string;
    prefix?: string;
    placeholder?: string;
    hint?: string;
    forceDetails?: boolean;
    variant?: 'primary' | 'secondary' | 'tertiary';
    type?: 'text' | 'password' | 'date' | 'number';
    focusHelper?: boolean;
    rules?: ((value: any) => boolean | string)[];
    validationValue?: string[] | number[] | Dayjs[] | string | number | Dayjs;
    validateKeyDown?: (event: KeyboardEvent) => void;
    modelValue?: string;
};

const props = withDefaults(defineProps<DeepiTextFieldProps>(), {
    variant: 'tertiary',
    type: 'text',
    focusHelper: false
});

const emit = defineEmits(['update:modelValue']);

const textField = ref<InstanceType<typeof VTextField>>();

const hasFocus = ref(false);

const iconColor = computed((): string => {
    switch (props.variant) {
        case 'primary':
            return 'primary';
        case 'secondary':
            return 'on-secondary';
        default:
            return 'secondary';
    }
});

const classes = computed((): string[] => {
    const classes: string[] = [props.variant];
    // The comparison is important here because isValid is null before first validation
    if (textField.value && textField.value.isValid === false) {
        classes.push('error');
    }
    return classes;
});
</script>

<style scoped lang="scss">
@use 'DeepiTextField.scss';
</style>